import { Result } from "../monads/Result";
import { bankersRound } from "../numbers/bankersRound";

type CurrencyCode = "SEK";

class MustBeFractionalUnitError extends Error {
  public static readonly code = "MUST_BE_FRACTIONAL_UNIT";
  public readonly code = MustBeFractionalUnitError.code;
}

type MoneyOperation =
  | { type: "multiply"; factor: number }
  | { type: "add"; other: Money }
  | { type: "subtract"; other: Money };

type MoneyJSON = {
  amount: number;
  currencyCode: CurrencyCode;
};

export class Money {
  private initialAmount: number;
  public currencyCode: CurrencyCode = "SEK";
  private operations: MoneyOperation[] = [];

  protected constructor(
    initialAmount: number,
    currencyCode: CurrencyCode = "SEK",
    operations: MoneyOperation[] = [],
  ) {
    this.initialAmount = initialAmount;
    this.currencyCode = currencyCode;
    this.operations = operations;
  }

  toJSON(): MoneyJSON {
    return {
      amount: this.amount,
      currencyCode: this.currencyCode,
    };
  }

  static fromJSON(json: ReturnType<Money["toJSON"]>): Money {
    return new Money(json.amount, json.currencyCode, []);
  }

  static zero() {
    return new Money(0);
  }

  static fromCents(amount: number): Result<Money, MustBeFractionalUnitError> {
    if (!Number.isSafeInteger(amount)) {
      return Result.error(new MustBeFractionalUnitError());
    }

    return Result.ok(new Money(amount));
  }

  get amount() {
    const resolvedAmount = this.operations.reduce(
      (result: number, operation: MoneyOperation): number => {
        switch (operation.type) {
          case "multiply":
            return result * operation.factor;
          case "add":
            return result + operation.other.amount;
          case "subtract":
            return result - operation.other.amount;
          default: {
            throw new Error(`Unsupported operation ${operation}`);
          }
        }
      },
      this.initialAmount,
    );
    return bankersRound(resolvedAmount);
  }

  roundToWholeUnit() {
    const amount = this.amount;
    const diffToFloor = amount % 100;
    const diffToCeil = 100 - (amount % 100);
    const floor = new Money(amount - diffToFloor, this.currencyCode, []);
    const ceiling = new Money(amount + diffToCeil, this.currencyCode, []);

    if (diffToFloor < diffToCeil) {
      return floor;
    }

    return ceiling;
  }

  format({ includeDecimals = true } = {}) {
    if (!includeDecimals) {
      return new Intl.NumberFormat("sv", {
        style: "currency",
        currency: "SEK",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(this.toFloat());
    }

    return new Intl.NumberFormat("sv", {
      style: "currency",
      currency: this.currencyCode,
    }).format(this.toFloat());
  }

  static fromFloat(amount: number) {
    const fractionalUnits = bankersRound(amount * 100);
    return new Money(fractionalUnits);
  }

  toFloat() {
    return this.amount / 100;
  }

  add(other: Money) {
    return new Money(this.initialAmount, this.currencyCode, [
      ...this.operations,
      { type: "add", other },
    ]);
  }

  subtract(other: Money) {
    return new Money(this.initialAmount, this.currencyCode, [
      ...this.operations,
      { type: "subtract", other },
    ]);
  }

  multiply(factor: number) {
    return new Money(this.initialAmount, this.currencyCode, [
      ...this.operations,
      { type: "multiply", factor },
    ]);
  }
}
